import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, Paper, TextField, Typography,
} from '@mui/material';
import { LeadService } from '../../services/lead.service';
import {
  Lead, LEAD_STATUS_LABELS, LEAD_SOURCE_LABELS,
} from '../../interfaces/crm/crm.interface';
import LeadForm from './LeadForm';
import Field from './Field';
import LeadConvertDialog from './LeadConvertDialog';
import ActivityTimeline from './ActivityTimeline';
import LogActivityDialog from './LogActivityDialog';
import { useToast } from '../../context/ToastContext';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { CRM_ATTACHMENT_KINDS } from '../../services/attachment.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.CRM_MANAGE);

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [activityReload, setActivityReload] = useState(0);

  const [disqualifyOpen, setDisqualifyOpen] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [disqualifying, setDisqualifying] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await LeadService.getById(id);
      setLead(res);
    } catch (e) {
      toast.showApiError(e, 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  const handleDisqualify = async () => {
    if (!lead || disqualifyReason.trim() === '') return;
    setDisqualifying(true);
    try {
      await LeadService.disqualify(lead.id, { reason: disqualifyReason.trim() });
      toast.showSuccess('Lead disqualified');
      setDisqualifyOpen(false);
      setDisqualifyReason('');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to disqualify lead');
    } finally {
      setDisqualifying(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!lead) return <Box sx={{ p: 3 }}><Typography>Lead not found.</Typography></Box>;

  const isConverted = lead.status === 'CONVERTED';
  const isTerminal = isConverted || lead.status === 'DISQUALIFIED';

  return (
    <Box sx={{ p: 3 }} data-help-page="crm.leadDetail.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{lead.name}</Typography>
          <Chip label={LEAD_STATUS_LABELS[lead.status]} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canManage && <Button data-help="crm.leadDetail.edit" variant="outlined" onClick={() => setEditOpen(true)}>Edit</Button>}
          {canManage && !isTerminal && (
            <Button data-help="crm.leadDetail.convert" variant="contained" onClick={() => setConvertOpen(true)}>
              Convert
            </Button>
          )}
          {canManage && !isTerminal && (
            <Button data-help="crm.leadDetail.disqualify" variant="outlined" color="error" onClick={() => { setDisqualifyOpen(true); setDisqualifyReason(''); }}>
              Disqualify
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Details</Typography>
            <Field label="Company" value={lead.companyName} />
            <Field label="Email" value={lead.email} />
            <Field label="Phone" value={lead.phone} />
            <Field label="Source" value={LEAD_SOURCE_LABELS[lead.source]} />
            {lead.disqualifyReason && <Field label="Disqualify Reason" value={lead.disqualifyReason} />}
            <Field label="Notes" value={lead.notes} />
            {lead.convertedOpportunityId && (
              <Button
                data-help="crm.leadDetail.viewOpportunity"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => navigate(`/crm/opportunities/${lead.convertedOpportunityId}`)}
              >
                View Opportunity
              </Button>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Activity</Typography>
              {canManage && <Button data-help="crm.leadDetail.logActivity" size="small" variant="outlined" onClick={() => setLogOpen(true)}>Log Activity</Button>}
            </Box>
            <Divider sx={{ mb: 1 }} />
            <ActivityTimeline ownerType="LEAD" ownerId={lead.id} reloadKey={activityReload} />
          </Paper>
        </Grid>
      </Grid>

      <LeadForm
        open={editOpen}
        lead={lead}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />

      <LeadConvertDialog
        open={convertOpen}
        lead={lead}
        onClose={() => setConvertOpen(false)}
        onConverted={opp => navigate(`/crm/opportunities/${opp.id}`)}
      />

      <LogActivityDialog
        open={logOpen}
        ownerType="LEAD"
        ownerId={lead.id}
        onClose={() => setLogOpen(false)}
        onLogged={() => setActivityReload(k => k + 1)}
      />

      <Dialog open={disqualifyOpen} onClose={() => !disqualifying && setDisqualifyOpen(false)} fullWidth maxWidth="xs" data-help-page="crm.leadDetail.disqualifyDialog">
        <DialogTitle>Disqualify Lead</DialogTitle>
        <DialogContent data-help-section="crm.leadDetail.disqualifyDialog.section.reason">
          <TextField
            data-help="crm.leadDetail.disqualifyDialog.reason"
            autoFocus
            fullWidth
            label="Reason"
            value={disqualifyReason}
            onChange={e => setDisqualifyReason(e.target.value)}
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="crm.leadDetail.disqualifyDialog.cancel" onClick={() => setDisqualifyOpen(false)} disabled={disqualifying}>Cancel</Button>
          <Button data-help="crm.leadDetail.disqualifyDialog.confirm" variant="contained" color="error" onClick={handleDisqualify} disabled={disqualifying || disqualifyReason.trim() === ''}>
            {disqualifying ? <CircularProgress size={20} /> : 'Disqualify'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box data-help-section="sales.lead.attachments">
        <AttachmentsPanel entityType="LEAD" entityId={lead.id} canManage={canManage} kinds={CRM_ATTACHMENT_KINDS} />
      </Box>
    </Box>
  );
};

export default LeadDetail;
